import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ProtectedLayout } from 'hoc';
import { useLocation, useNavigate, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useUpdateResult } from 'hooks/updateResult';
import { type EditorAutosaveDeps, useAutosaveStatus } from 'hooks/useAutosaveStatus';
import { useVitalParametersEditor } from 'hooks/useVitalParametersEditor';
import { useDiagnosedIcdsEditor } from 'hooks/useDiagnosedIcdsEditor';
import { useEbmProposalsEditor } from 'hooks/useEbmProposalsEditor';
import { useResultSectionsEditor } from 'hooks/useResultSectionsEditor';
import { useResultExport } from 'hooks/useResultExport';
import { useAnalytics } from 'analytics';
import { ANALYTICS_EVENTS } from 'analytics/events';

import { ExportStatusValue, type ResultData, ResultTemplateKey } from 'types/result';
import type { PreventionAlert } from 'types/preventionAlerts';
import { ResultSummaryTab } from 'widgets/results/ResultSummaryTab';
import { ResultFeedbackWidget } from 'widgets/results/ResultFeedbackWidget';
import { PatientHeader } from 'widgets';
import { ImmunizationsSection, VaccinationDialog } from 'widgets/immunization';
import { useImmunizationEditor } from 'hooks/useImmunizationEditor';
import { immunizationKeys } from 'hooks/useImmunizations';
import {
  buildTranscriptText,
  formatDateToDisplayString,
  formatDuration,
  formatReadableTime,
} from 'utils';
import { useGetOneResult } from 'hooks/getResults';
import { useRetrySuggestions } from 'hooks/retrySuggestions';
import { useGetExportStatus } from 'hooks/exportResultAsync';
import { useBillingScheinStatus } from 'hooks/useBillingScheinStatus';
import {
  Container,
  Heading,
  ImmunizationSlot,
  ResultSectionsWrapper,
  SubHeading,
  SummaryTranscriptTab,
  SummaryTranscriptTabs,
} from './result.styles';
import { ExportStatusWidget, TranscriptSection } from 'widgets/results';
import type { RawAnamnesisRecord } from 'types/anamnesis';
import { toAnamnesisRecord } from 'utils/anamnesis';
import {
  buildPatientNameLine,
  buildPatientSubline,
  formatPatientName,
} from 'utils/visitReasonDisplay';
import { useRecordingSession } from 'providers/RecordingSessionProvider';
import { usePatientSync } from 'providers/PatientSyncProvider';
import { VitalParametersEditorProvider } from 'providers/VitalParametersEditorProvider';
import { EbmProposalsEditorProvider } from 'providers/EbmProposalsEditorProvider';
import { ResultSectionsEditorProvider } from 'providers/ResultSectionsEditorProvider';
import { DiagnosedIcdsEditorProvider } from 'providers/DiagnosedIcdsEditorProvider';
import { AutosaveStatusProvider } from 'providers/AutosaveStatusProvider';
import { toast } from 'sonner';

interface LocationState {
  result: ResultData;
  elapsedTime?: number;
  languageCode?: string;
  fromRecording?: boolean;
}

export default function ResultScreen() {
  const { id } = useParams<{ id: string }>();

  return <ResultScreenContent key={id} />;
}

function ResultScreenContent() {
  const navigate = useNavigate();
  const { id: resultIdFromParams } = useParams<{ id: string }>();
  const location = useLocation();
  const { t } = useTranslation();
  const { trackEvent } = useAnalytics();
  const resultFromProps = (location.state as LocationState)?.result;
  const resultId = resultIdFromParams || resultFromProps?.id;
  const fromRecording = Boolean((location.state as LocationState)?.fromRecording);
  const session = useRecordingSession();
  const isLiveResult = session.icdResultId !== null && session.icdResultId === String(resultId);
  const icdCodes = isLiveResult ? session.icdCodes : null;
  const icdStatus = isLiveResult ? session.icdStatus : 'idle';
  const icdProcessingSlow = isLiveResult && session.icdProcessingSlow;
  const icdCodesReceivedAt = isLiveResult ? session.icdCodesReceivedAt : null;
  const [resultMainTab, setResultMainTab] = useState<'summary' | 'transcript'>('summary');
  const [activeDictation, setActiveDictation] = useState<string | undefined>(undefined);
  const [influenzaDialogAlert, setInfluenzaDialogAlert] = useState<PreventionAlert | null>(null);

  const { data: conversation, isLoading } = useGetOneResult(Number(resultId), {
    enabled: !resultFromProps && !!resultIdFromParams,
  });

  const queryClient = useQueryClient();

  const { mutateAsync: autosaveEditAsync } = useUpdateResult(Number(resultId));

  const notifyRetrySuggestionsFailed = useCallback(() => {
    toast.error(t('result.suggestions.retryFailed'));
  }, [t]);

  const { mutate: retrySuggestions, isPending: isRetryingSuggestions } = useRetrySuggestions(
    Number(resultId),
    notifyRetrySuggestionsFailed
  );

  const { data: exportStatus } = useGetExportStatus(Number(resultId), {
    enabled: !!resultId,
    activeRefetchIntervalMs: 2500,
  });

  const patientMedatixxId =
    conversation?.patient?.medatixxId ?? resultFromProps?.patient?.medatixxId ?? null;
  const {
    data: scheinStatus,
    isFetching: isScheinChecking,
    refetch: refetchScheinStatus,
  } = useBillingScheinStatus(patientMedatixxId);

  const [hasUserEdits, setHasUserEdits] = useState(false);
  const immunizationsBlockRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setHasUserEdits(false);
  }, [exportStatus?.id]);

  const { autoStatus, setAutoStatus, autosaveTimeouts } = useAutosaveStatus(resultId);

  const result = conversation || resultFromProps;
  const interactionId = result?.interaction?.interactionId;

  const {
    immunizations,
    isLoading: isLoadingImmunizations,
    createImmunization,
    updateImmunization,
    removeImmunization,
    flushPendingEdits: flushImmunizationEdits,
  } = useImmunizationEditor({
    interactionId,
    medatixxPatientId: patientMedatixxId,
    onUserEdit: () => setHasUserEdits(true),
  });
  const hasOpenImmunization = immunizations.some((immunization) => !immunization.transferredAt);
  const hasPendingImmunization = immunizations.some(
    (immunization) => !immunization.transferredAt && !immunization.locked
  );
  const exportFailed =
    exportStatus?.status === ExportStatusValue.Failure ||
    exportStatus?.status === ExportStatusValue.Error;

  // The poller marks immunizations as transferred while the export runs; the block only
  // learns about it when the export status moves.
  useEffect(() => {
    if (!interactionId) return;
    void queryClient.invalidateQueries({ queryKey: immunizationKeys.byInteraction(interactionId) });
  }, [queryClient, interactionId, exportStatus?.id, exportStatus?.status]);

  const editorAutosaveDeps: EditorAutosaveDeps = {
    autosaveEditAsync,
    autosaveTimeouts,
    setAutoStatus,
    setHasUserEdits,
  };

  const vitalParametersEditor = useVitalParametersEditor({
    resultId,
    vitalParameters: result?.vitalParameters,
    ...editorAutosaveDeps,
  });
  const { vitalParameterSelectionMap, pendingVitalParameters, clearPendingVitalParameters } =
    vitalParametersEditor;

  const diagnosedIcdsEditor = useDiagnosedIcdsEditor({
    result,
    resultId,
    fromRecording,
    icdCodes,
    icdStatus,
    t,
    ...editorAutosaveDeps,
  });
  const {
    icdPending,
    diagnosedIcdsTitle,
    pendingDiagnosisSelection,
    icdCompleteBannerVisible,
    dismissIcdCompleteBanner,
  } = diagnosedIcdsEditor;

  const ebmProposalsEditor = useEbmProposalsEditor({
    resultId,
    durationSeconds: result?.durationSeconds,
    ...editorAutosaveDeps,
  });
  const { pendingEbmSelection, pendingEbmDuration } = ebmProposalsEditor;

  const resultSectionsEditor = useResultSectionsEditor({
    result,
    trackEvent,
    t,
    ...editorAutosaveDeps,
  });
  const { saveEditsBeforeExport, flushPendingEdits, adoptSuggestionSections } =
    resultSectionsEditor;
  const { pendingPatient, switchToPendingPatient, setConsultationPatientId } = usePatientSync();
  const pendingPatientRef = useRef(pendingPatient);
  pendingPatientRef.current = pendingPatient;
  useEffect(() => {
    setConsultationPatientId(patientMedatixxId);
    return () => setConsultationPatientId(null);
  }, [patientMedatixxId, setConsultationPatientId]);

  const { isExporting, handleExport, handleRetryExport } = useResultExport({
    resultId,
    anamnesisId: result?.anamnesisSummary?.id,
    vitalParameterSelectionMap,
    exportStatus,
    saveEditsBeforeExport,
    pendingDiagnosisSelection,
    pendingEbmSelection,
    pendingEbmDuration,
    pendingVitalParameters,
    clearPendingVitalParameters,
    hasOpenImmunization,
    flushImmunizationEdits,
    t,
  });

  const navigateBack = () => navigate(fromRecording ? -2 : -1);
  const patientOverviewAfterRecording =
    fromRecording && patientMedatixxId
      ? `/patient-overview/${encodeURIComponent(patientMedatixxId)}`
      : null;

  const leaveResult = () =>
    patientOverviewAfterRecording
      ? navigate(patientOverviewAfterRecording, { replace: true })
      : navigateBack();

  const transferAndLeave = async (transfer: () => Promise<boolean>) => {
    trackEvent(ANALYTICS_EVENTS.RESULT_TRANSFER_SUMMARY);
    if (!(await transfer())) return;
    if (pendingPatientRef.current === null) {
      await leaveResult();
      return;
    }
    await navigateBack();
    switchToPendingPatient();
  };

  const anamnesisRecord = useMemo(() => {
    if (!result) {
      return null;
    }

    const record: RawAnamnesisRecord = {
      ...result.patient,
      visitReason: result.visitReason ?? undefined,
      summary: result.anamnesisSummary ?? undefined,
    };

    return toAnamnesisRecord(record);
  }, [result]);

  const transcriptText = useMemo(() => buildTranscriptText(result), [result]);
  const hasRecording = !result?.interaction?.withoutRecording;

  const formattedDate = result ? formatDateToDisplayString(result.timestamp, true) : '';
  const formattedTime = result ? formatReadableTime(result.timestamp) : '';
  const formattedDuration = result ? formatDuration(result.durationSeconds) : '';

  const sectionKeys = useMemo<{ key: ResultTemplateKey; title: string }[]>(
    () => [
      { key: ResultTemplateKey.Anamnesis, title: t('result.section.anamnesis') },
      { key: ResultTemplateKey.Findings, title: t('result.section.findings') },
      { key: ResultTemplateKey.Diagnosis, title: diagnosedIcdsTitle },
      { key: ResultTemplateKey.Therapy, title: t('result.section.therapy') },
      { key: ResultTemplateKey.Procedure, title: t('result.section.procedure') },
      { key: ResultTemplateKey.Comment, title: t('result.section.comment') },
    ],
    [t, diagnosedIcdsTitle]
  );

  const sectionsEditorState = useMemo(
    () => ({
      ...resultSectionsEditor,
      sectionKeys,
      activeDictation,
      setActiveDictation,
    }),
    [resultSectionsEditor, sectionKeys, activeDictation]
  );

  const patientHeader = anamnesisRecord
    ? {
        title: formatPatientName(anamnesisRecord.patientName),
        subtitle: buildPatientSubline(anamnesisRecord.birthdate, anamnesisRecord.medatixxPatientId),
      }
    : { title: t('result.title') };

  if (isLoading && !resultFromProps) {
    return (
      <ProtectedLayout centered hasBackButton={false}>
        <div>{t('general.loading')}</div>
      </ProtectedLayout>
    );
  }

  if (!result && !resultIdFromParams) {
    return (
      <ProtectedLayout centered hasBackButton={false}>
        <div>{t('result.noConversationData')}</div>
      </ProtectedLayout>
    );
  }

  if (!result) {
    return (
      <ProtectedLayout centered hasBackButton={false}>
        <div>{t('result.noResultsAvailable')}</div>
      </ProtectedLayout>
    );
  }

  return (
    <ProtectedLayout
      centered
      hasBackButton={false}
      title={patientHeader.title}
      subtitle={patientHeader.subtitle}
      interactionId={interactionId}
    >
      <Container>
        <PatientHeader
          anamnesis={anamnesisRecord}
          showPreventionAlerts
          preventions={{
            screen: 'result',
            defaultOpen: true,
            interactionId,
            onRecordInfluenza: setInfluenzaDialogAlert,
          }}
          visitReason={anamnesisRecord?.visitReason}
          collapsibleSummary
        />
        <ResultSectionsWrapper>
          <Heading>
            {t('result.conversationAt')} {formattedDate}
          </Heading>
          <SubHeading>
            {formattedTime} - {formattedDuration}
            {t('result.ebmProposals.minutesShort')}
          </SubHeading>
          {hasRecording && (
            <SummaryTranscriptTabs aria-label={t('result.summaryTranscriptTabs')}>
              <SummaryTranscriptTab
                $isActive={resultMainTab === 'summary'}
                id="result-tab-summary"
                aria-selected={resultMainTab === 'summary'}
                aria-controls="result-panel-summary"
                onClick={() => setResultMainTab('summary')}
              >
                {t('result.summaryTab')}
              </SummaryTranscriptTab>
              <SummaryTranscriptTab
                $isActive={resultMainTab === 'transcript'}
                id="result-tab-transcript"
                aria-selected={resultMainTab === 'transcript'}
                aria-controls="result-panel-transcript"
                onClick={() => setResultMainTab('transcript')}
              >
                {t('result.transcriptTab')}
              </SummaryTranscriptTab>
            </SummaryTranscriptTabs>
          )}
          {!hasRecording || resultMainTab === 'summary' ? (
            <AutosaveStatusProvider value={autoStatus}>
              <VitalParametersEditorProvider value={vitalParametersEditor}>
                <EbmProposalsEditorProvider value={ebmProposalsEditor}>
                  <ResultSectionsEditorProvider value={sectionsEditorState}>
                    <DiagnosedIcdsEditorProvider value={diagnosedIcdsEditor}>
                      <ResultSummaryTab result={result} />
                    </DiagnosedIcdsEditorProvider>
                  </ResultSectionsEditorProvider>
                </EbmProposalsEditorProvider>
              </VitalParametersEditorProvider>
            </AutosaveStatusProvider>
          ) : (
            <div
              id="result-panel-transcript"
              role="tabpanel"
              aria-labelledby="result-tab-transcript"
            >
              <TranscriptSection transcriptText={transcriptText} mode="standalone" />
            </div>
          )}
        </ResultSectionsWrapper>
        <ImmunizationSlot>
          <ImmunizationsSection
            ref={immunizationsBlockRef}
            immunizations={immunizations}
            exportFailed={exportFailed}
            isExporting={isExporting}
            onUpdate={updateImmunization}
            onRemove={removeImmunization}
          />
        </ImmunizationSlot>
        <ResultFeedbackWidget interactionId={interactionId} />
      </Container>
      {influenzaDialogAlert && interactionId && patientMedatixxId ? (
        <VaccinationDialog
          availableReasons={influenzaDialogAlert.availableVaccinationReasons ?? []}
          onSubmit={createImmunization}
          focusAfterClose={immunizationsBlockRef}
          patientLine={buildPatientNameLine(
            anamnesisRecord?.patientName,
            anamnesisRecord?.birthdate,
            patientMedatixxId
          )}
          onClose={() => setInfluenzaDialogAlert(null)}
        />
      ) : null}
      <ExportStatusWidget
        exportStatus={exportStatus}
        // Until the immunization list is in, the export would not know to include it.
        isTransferring={isExporting || isLoadingImmunizations}
        hasEdits={hasUserEdits || hasPendingImmunization}
        patient={result?.patient}
        scheinStatus={scheinStatus ?? null}
        isScheinChecking={isScheinChecking}
        icdPending={icdPending}
        icdProcessingSlow={icdProcessingSlow}
        icdCodesReceivedAt={icdCodesReceivedAt}
        icdJustCompleted={icdCompleteBannerVisible}
        suggestionsFailed={Boolean(result?.suggestionsFailed)}
        isRetryingSuggestions={isRetryingSuggestions}
        onDismissIcdComplete={dismissIcdCompleteBanner}
        onScheinRecheck={() => {
          void refetchScheinStatus();
        }}
        onRetrySuggestions={() => {
          trackEvent(ANALYTICS_EVENTS.RESULT_SUGGESTIONS_RETRY);
          void flushPendingEdits().then(
            (sentText) =>
              retrySuggestions(undefined, {
                onSuccess: (fresh) => adoptSuggestionSections(fresh, sentText),
              }),
            notifyRetrySuggestionsFailed
          );
        }}
        onTransfer={() => void transferAndLeave(handleExport)}
        onRetry={() => void transferAndLeave(handleRetryExport)}
        onDismiss={() => {
          trackEvent(ANALYTICS_EVENTS.RESULT_CLOSE);
          void leaveResult();
        }}
      />
    </ProtectedLayout>
  );
}
